// @flow

import React from 'react';
import styled, { css } from 'styled-components';
import breakpoint from 'styled-components-breakpoint';
import { ifProp } from 'styled-tools';
import { palette } from 'styled-theme';
import { FormattedMessage } from 'react-intl';
import { get } from 'js-cookie';
import { isMobile } from 'react-device-detect';

import NavLink from '../../../Link';
import burgerSvg from './assets/burger.svg';
import Dropdown from '../Dropdown';

import Business from './assets/business.svg';
import { UserInfoConsumer } from '../../../../contexts/UserInfo';

import Telegram from './assets/telegram.svg';

type Props = {
  isOpen: boolean,
  checkStatus: Array<boolean>,
  onToggleClick: () => any,
  onMenuClick: () => any,
  changeCheckStatus: () => any,
};

const Link = styled(NavLink)`
  transition: opacity 0.2s;
  text-decoration: none;
  color: #D4D4D4;
  box-sizing: border-box;

  &:hover {
    color: #fff;
  }
  &.not-active {
    pointer-events: none;
    cursor: default;
    text-decoration: none;
    color: white;
  }
  &[aria-current='true'] {
    opacity: 1;
    cursor: default;
    font-weight: normal;

    ${breakpoint('desktop')`
      font-weight: normal;
    `};
    }
  }
`;

const TelegramLink = styled.a`
  transition: opacity 0.2s;
  text-decoration: none;
  color: #d4d4d4;
  box-sizing: border-box;
`;

const ColoredLink = Link.extend`
  color: ${palette('success', 0)};
  font-weight: bolder;
  opacity: 0.8;
  border: 1px solid ${palette('success', 0)};
  padding: 7px 20px !important;
  border-radius: 4px;
  justify-content: center;

  &:hover {
    opacity: 0.95;
    color: ${palette('success', 0)};
  }

  &[aria-current='true'] {
    font-weight: bold;
    }
  }
`;

const Wrapper = styled.div`
  font-size: 1.1em;
  ${breakpoint('desktop')`
    font-size: 1em;
  `};
`;

const Toggle = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  padding: 0 20px;
  width: 70px;
  height: 100%;
  background: url(${burgerSvg}) 50%/30px no-repeat;
  border: none;
  cursor: pointer;
  box-sizing: border-box;

  &:focus {
    outline: none;
  }

  ${breakpoint('desktop')`
    display: none;
  `};

  ${ifProp(
    'isOpen',
    css`
      background-color: #3d3e44;
    `,
  )};
`;

const List = styled.ul`
  display: none;
  flex-flow: column;
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 10;
  width: 100%;
  background-color: #3d3e44;
  box-sizing: border-box;

  ${ifProp(
    'isOpen',
    css`
      display: flex;
    `,
  )};

  ${breakpoint('desktop')`
    padding: 20px;
    display: flex;
    position: static;
    background: none;
    flex-flow: row;
    flex-wrap: wrap;
    padding: 0;
  `};
`;

const Item = styled.li`
  padding: 0 10px !important;
  position: relative;
  order: 1;
  ${breakpoint('desktop')`
    padding: 0 25px !important;
  `}
  &:last-child {
    margin-right: 0;
    order: 0;

    ${breakpoint('desktop')`
      order: 1;
    `}
  }

  a {
    display: flex;
    padding: 10px 0;
    height: 100%;
    align-items: center;
    font-size: 17px;
    &.sublink {
      font-size: 12px;
      color: #d4d4d4;

      ${breakpoint('desktop')`
        font-size: 17px;
      `}
    }
  }

  &.last-item a {
    padding: 20px 17px;
    ${breakpoint('desktop')`
      padding: 10px;
    `}
  }

  &.activate-btn {
    margin-bottom: 10px;
    ${breakpoint('desktop')`
      padding-right: 0;
    `}
  }
`;

const ImgTag = styled.img`
  margin-left: 6px;
`;

const BusinessTag = () => <ImgTag src={Business} alt="" />;

const Menu = (props: Props) => (
  <UserInfoConsumer>
    {({
      isAuthenticated,
      subscriptionIsActive,
      subscriptionIsChild,
      subscriptionIsBasic,
      subscriptionIsPro,
    }) => {
      return (
        <Wrapper>
          <Toggle onClick={props.onToggleClick} isOpen={props.isOpen} />
          <List isOpen={props.isOpen}>
            <Dropdown
              checkStatus={props.checkStatus[0]}
              changeCheckStatus={() => props.changeCheckStatus(0)}
              dropDownUrl="/"
              dropDownTitle={
                <FormattedMessage id="header.ads" defaultMessage="Объявления" />
              }
            >
              <Item onClick={props.onMenuClick}>
                <Link to="/business/effectiveness/" className="sublink">
                  <FormattedMessage
                    id="header.ads.queries"
                    defaultMessage="Список всех объявлений"
                  />
                </Link>
              </Item>
              <Item onClick={props.onMenuClick}>
                <Link
                  to="/business/advanced-effectiveness/"
                  className="sublink"
                >
                  <FormattedMessage
                    id="header.ads.report"
                    defaultMessage="Общий отчет по объявлениям"
                  />
                  {!subscriptionIsActive && <BusinessTag />}
                </Link>
              </Item>
            </Dropdown>

            <Dropdown
              checkStatus={props.checkStatus[1]}
              changeCheckStatus={() => props.changeCheckStatus(1)}
              dropDownUrl="/"
              dropDownTitle={
                <FormattedMessage
                  id="header.accounts"
                  defaultMessage="Аккаунты"
                />
              }
            >
              <Item onClick={props.onMenuClick}>
                <Link to="/control/accounts/" className="sublink">
                  <FormattedMessage
                    id="header.accounts.connectedAccounts"
                    defaultMessage="Подключенные аккаунты"
                  />
                  {subscriptionIsChild && <BusinessTag />}
                </Link>
              </Item>
              <Item onClick={props.onMenuClick}>
                <Link to="/control/history/" className="sublink">
                  <FormattedMessage
                    id="header.accounts.history"
                    defaultMessage="История действий"
                  />
                  {subscriptionIsChild && <BusinessTag />}
                </Link>
              </Item>
            </Dropdown>

            <Dropdown
              checkStatus={props.checkStatus[2]}
              changeCheckStatus={() => props.changeCheckStatus(2)}
              dropDownUrl="/"
              dropDownTitle={
                <FormattedMessage
                  id="header.finance"
                  defaultMessage="Финансы"
                />
              }
            >
              <Item onClick={props.onMenuClick}>
                <Link to="/control/funds/">
                  <FormattedMessage
                    id="header.finance.transfer"
                    defaultMessage="Перевод средств аккаунтам"
                  />
                  {(!subscriptionIsActive || subscriptionIsChild) && (
                    <BusinessTag />
                  )}
                </Link>
              </Item>
              <Item onClick={props.onMenuClick}>
                <Link to="/business/finance/">
                  <FormattedMessage
                    id="header.finance.report"
                    defaultMessage="Финансовый отчет"
                  />
                  {!subscriptionIsActive && <BusinessTag />}
                </Link>
              </Item>
              <Item onClick={props.onMenuClick}>
                <Link to="/subscribe/business/">
                  <FormattedMessage
                    id="header.finance.requisites"
                    defaultMessage="Реквизиты"
                  />
                  {subscriptionIsPro && <BusinessTag />}
                </Link>
              </Item>
              {/*// <Item onClick={props.onMenuClick}>
              //   <Link to="/subscribe/pro/">
              //     <FormattedMessage
              //       id="footer.activation.pro"
              //       defaultMessage="Настройки аккаунта"
              //     />
              //     {subscriptionIsBasic && <BusinessTag />}
              //   </Link>
            // </Item>*/}

              <Item onClick={props.onMenuClick}>
                <Link to="/discounts/">
                  <FormattedMessage
                    id="header.finance.discounts"
                    defaultMessage="Скидки и бонусы"
                  />
                </Link>
              </Item>
            </Dropdown>

            <Dropdown
              checkStatus={props.checkStatus[3]}
              changeCheckStatus={() => props.changeCheckStatus(3)}
              dropDownUrl="/"
              dropDownTitle={
                <FormattedMessage
                  id="header.analytics"
                  defaultMessage="Аналитика"
                />
              }
            >
              <Item onClick={props.onMenuClick}>
                <Link to="/analysis/trends/">
                  <FormattedMessage
                    id="common.trends"
                    defaultMessage="Поисковые тренды"
                  />
                  {!subscriptionIsActive && <BusinessTag />}
                </Link>
              </Item>
              <Item onClick={props.onMenuClick}>
                <Link to="/analysis/search/queries/">
                  <FormattedMessage
                    id="common.topQueries"
                    defaultMessage="Топ поисковых запросов"
                  />
                  {!subscriptionIsActive && <BusinessTag />}
                </Link>
              </Item>
              <Item onClick={props.onMenuClick}>
                <Link to="/analysis/search/analytics/">
                  <FormattedMessage
                    id="common.analyticsCompare"
                    defaultMessage="Популярность слов в поиске"
                  />
                  {!subscriptionIsActive && <BusinessTag />}
                </Link>
              </Item>
              <Item onClick={props.onMenuClick}>
                <Link to="/site/adverts/">
                  <FormattedMessage
                    id="common.priceAnalytics"
                    defaultMessage="Оценка стоимости товаров"
                  />
                  {!subscriptionIsActive && <BusinessTag />}
                </Link>
              </Item>
              <Item onClick={props.onMenuClick}>
                <Link to="/site/statistics/visits/">
                  <FormattedMessage
                    id="common.visits"
                    defaultMessage="Время посещаемости категорий"
                  />
                  {!subscriptionIsActive && <BusinessTag />}
                </Link>
              </Item>
              <Item onClick={props.onMenuClick}>
                <Link to="/site/statistics/republish/">
                  <FormattedMessage
                    id="common.republish"
                    defaultMessage="Время публикации объявлений"
                  />
                  {!subscriptionIsActive && <BusinessTag />}
                </Link>
              </Item>
            </Dropdown>

            <Item onClick={props.onMenuClick} className="last-item">
              <Link to="/api/">API</Link>
            </Item>

            <Item onClick={props.onMenuClick} className="last-item">
              <TelegramLink href="https://t.me/simpals_999" target="_blank">
                <ImgTag src={Telegram} alt="telegram" />
              </TelegramLink>
            </Item>

            <Item
              onClick={() => {
                /* GA event */
                (window.dataLayer || []).push({
                  event: 'business_activate',
                  action: 'start',
                  location: 'business_999',
                  button_location: 'header',
                });
                props.onMenuClick();
              }}
              className="activate-btn"
            >
              <ColoredLink id="activate-header" to="/tariffs/">
                {subscriptionIsActive ? (
                  <FormattedMessage
                    id="header.button.tarif"
                    defaultMessage="Тарифы"
                  />
                ) : (
                  <FormattedMessage
                    id="header.button.activate"
                    defaultMessage="Активировать"
                  />
                )}
              </ColoredLink>
            </Item>
          </List>
        </Wrapper>
      );
    }}
  </UserInfoConsumer>
);

export default Menu;



// WEBPACK FOOTER //
// ./src/components/Header/partials/Menu/index.js